import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { KeyRound, Eye, EyeOff, CheckCircle2, ArrowLeft } from 'lucide-react';
import { changePassword } from '../services/api';
import PasswordStrengthMeter from '../components/auth/PasswordStrengthMeter';
import HubLayout from '../components/common/HubLayout';

export default function ChangePassword() {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  const isValid = currentPassword && newPassword.length >= 8 && newPassword === confirmPassword &&
    (/[0-9]/.test(newPassword) || /[^a-zA-Z0-9]/.test(newPassword));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || loading) return;
    setLoading(true);
    setError(null);
    try {
      const result = await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      setSuccess(result);
    } catch (err) {
      const data = err.response?.data || {};
      setError({
        code: data.code || 'unknown',
        message: data.error || err.message || 'Something went wrong.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <HubLayout activeView="settings">
        <div className="min-h-[60vh] flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 rounded-full bg-[#EDF3E0] flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={32} color="#556B2F" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>Password changed</h2>
            <p className="text-base text-gray-600 mb-2">Your password has been updated successfully.</p>
            {success.sessions_revoked > 0 && (
              <p className="text-sm text-gray-500 mb-4">{success.sessions_revoked} other device{success.sessions_revoked !== 1 ? 's were' : ' was'} signed out. A confirmation email was sent.</p>
            )}
            <p className="text-sm text-gray-400 mb-6">If you didn't make this change, contact us right away.</p>
            <button onClick={() => navigate('/settings')} className="w-full bg-[#556B2F] text-white font-bold py-3 rounded-lg hover:bg-[#4A5D23]">
              Back to Settings
            </button>
          </div>
        </div>
      </HubLayout>
    );
  }

  return (
    <HubLayout activeView="settings">
      <div className="max-w-lg mx-auto p-6 py-10">
        <button onClick={() => navigate('/settings')} className="flex items-center gap-2 text-[#556B2F] font-semibold mb-6 text-sm">
          <ArrowLeft size={16} /> Back to Settings
        </button>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
          <div className="bg-[#556B2F] text-white p-5 flex items-center gap-3">
            <KeyRound size={22} />
            <div>
              <h1 className="text-xl font-bold">Change Password</h1>
              <p className="text-sm opacity-80">You'll stay signed in here. Other devices will be signed out.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {error && (
              <div role="alert" style={{
                background: '#FDECEA', border: '1.5px solid #F0B4AC', borderLeft: '5px solid #C0392B',
                borderRadius: 12, padding: '12px 14px', fontSize: 14,
              }}>
                <strong>{error.message}</strong>
                {error.code === 'incorrect_current_password' && (
                  <div style={{ marginTop: 6 }}>
                    <Link to="/forgot-password" style={{ color: '#C0392B', fontWeight: 600 }}>Forgot your password?</Link>
                  </div>
                )}
              </div>
            )}

            {/* Current password */}
            <div>
              <label className="block text-base font-bold text-gray-700 mb-2">Current Password</label>
              <div className="relative">
                <input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={e => { setCurrentPassword(e.target.value); setError(null); }}
                  className={`w-full px-4 py-4 text-lg border-2 rounded-lg focus:outline-none pr-12 ${error?.code === 'incorrect_current_password' ? 'border-[#E8A79C] bg-[#FFFBFA]' : 'border-gray-300 focus:border-[#556B2F]'}`}
                  placeholder="Enter current password"
                />
                <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showCurrent ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* New password */}
            <div>
              <label className="block text-base font-bold text-gray-700 mb-2">New Password</label>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className="w-full px-4 py-4 text-lg border-2 border-gray-300 rounded-lg focus:border-[#556B2F] focus:outline-none pr-12"
                  placeholder="Create new password"
                />
                <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showNew ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Confirm password */}
            <div>
              <label className="block text-base font-bold text-gray-700 mb-2">Confirm New Password</label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className={`w-full px-4 py-4 text-lg border-2 rounded-lg focus:outline-none pr-12 ${confirmPassword && newPassword !== confirmPassword ? 'border-[#E8A79C]' : 'border-gray-300 focus:border-[#556B2F]'}`}
                  placeholder="Confirm new password"
                />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-red-500 text-sm mt-1">Passwords don't match</p>
              )}
            </div>

            <PasswordStrengthMeter password={newPassword} confirmPassword={confirmPassword} />

            <button type="submit" disabled={!isValid || loading}
              className={`w-full py-4 rounded-lg font-bold text-lg transition-colors ${isValid && !loading ? 'bg-[#556B2F] text-white hover:bg-[#4A5D23] cursor-pointer' : 'bg-[#E4E2D6] text-[#A7A996] cursor-not-allowed'}`}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
    </HubLayout>
  );
}
